import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Mail, Shield, Cloud, Users, Globe, Server } from "lucide-react";
import { RelatedServices } from "@/components/RelatedServices";
import WebDesignLocations from '@/components/WebDesignLocations';

export default function EmailHosting() {
  const { openDrawer } = useQuoteDrawer();

  const features = [
    {
      title: "Professional Email Addresses",
      description: "Custom email addresses with your domain name for professional communication.",
      icon: <Mail className="h-8 w-8" />
    },
    {
      title: "Advanced Security",
      description: "Enterprise-level security with spam filtering and malware protection.",
      icon: <Shield className="h-8 w-8" />
    },
    {
      title: "Cloud Synchronization",
      description: "Access your emails from anywhere with cloud synchronization across devices.",
      icon: <Cloud className="h-8 w-8" />
    },
    {
      title: "Team Collaboration",
      description: "Shared calendars, contacts, and collaboration tools for your team.",
      icon: <Users className="h-8 w-8" />
    }
  ];

  const plans = [
    {
      name: "Starter",
      price: "$5",
      period: "per user/month",
      features: ["5GB Storage", "Custom Domain", "24/7 Support", "Mobile Access"]
    },
    {
      name: "Business",
      price: "$10",
      period: "per user/month",
      features: ["25GB Storage", "Advanced Security", "Shared Calendars", "Mobile Device Management"],
      popular: true
    },
    {
      name: "Enterprise",
      price: "$20",
      period: "per user/month",
      features: ["Unlimited Storage", "Advanced Compliance", "Dedicated Support", "Custom Integration"]
    }
  ];

  return (
    <>
      <SEO 
        title="Business Email Hosting UAE | First Soft UAE"
        description="Reliable business email hosting in Dubai & Sharjah. Secure, scalable solutions with 99.9% uptime guarantee and 24/7 support."
        keywords="email hosting uae, business email dubai, secure email sharjah"
      />

      <main>
      

        {/* Hero Section */}
        <section className="relative py-32 bg-gradient-to-br from-blue-50 to-indigo-50 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            {/* Animated email elements */}
            <div className="absolute top-0 left-0 w-full h-full opacity-10">
              {[...Array(15)].map((_, i) => (
                <div 
                  key={i}
                  className="absolute rounded-full bg-blue-200"
                  style={{
                    width: `${Math.random() * 10 + 5}px`,
                    height: `${Math.random() * 10 + 5}px`,
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animation: `pulse ${Math.random() * 5 + 3}s infinite alternate`
                  }}
                />
              ))}
            </div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div 
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center lg:text-left"
              >
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                    Email Hosting
                  </span>
                </h1>
                <p className="text-xl text-gray-600 mb-8 max-w-lg">
                  Professional, secure email solutions with enterprise-grade reliability and privacy.
                </p>
                
                <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-12">
                  {['99.9% Uptime', 'Enterprise Security', '25GB Storage', '24/7 Support'].map((feature) => (
                    <span key={feature} className="px-4 py-2 bg-white rounded-full text-gray-700 border border-gray-300 text-sm">
                      {feature}
                    </span>
                  ))}
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg font-medium text-white shadow-lg"
                  onClick={() => openDrawer()}
                >
                  Get Started
                </motion.button>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                {/* Glass morphism card */}
                <div className="relative bg-white/80 backdrop-blur-md border border-gray-200 rounded-xl p-8 shadow-lg overflow-hidden">
                  <div className="flex justify-center mb-8">
                    <Mail className="h-20 w-20 text-blue-600" />
                  </div>
                  
                  {/* Email interface mockup */}
                  <div className="relative h-48 bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden">
                    {/* Email header */}
                    <div className="h-8 bg-gray-50 flex items-center px-3 border-b border-gray-200">
                      <div className="flex space-x-2">
                        <div className="h-2 w-2 rounded-full bg-red-500"></div>
                        <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                        <div className="h-2 w-2 rounded-full bg-green-500"></div>
                      </div>
                      <div className="ml-2 text-xs text-gray-500 truncate">Inbox (12) - yourname@yourdomain.com</div>
                    </div>
                    
                    {/* Email list */}
                    <div className="p-2">
                      {['Important Client', 'Meeting Reminder', 'Newsletter', 'Support Ticket'].map((email, i) => (
                        <div key={i} className="flex items-center p-2 border-b border-gray-200 last:border-0">
                          <div className={`h-3 w-3 rounded-full mr-2 ${i === 0 ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
                          <div className="flex-1 truncate text-sm text-gray-700">{email}</div>
                          <div className="text-xs text-gray-500">{i === 0 ? 'Today' : i === 1 ? 'Yesterday' : 'Jun 20'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Email stats */}
                  <div className="grid grid-cols-2 gap-4">
                    {['99.9% Uptime', 'Encrypted', '25GB Storage', 'Unlimited'].map((stat) => (
                      <div key={stat} className="p-2 bg-gray-50 rounded-lg border border-gray-200 flex items-center">
                        <div className="h-6 w-6 bg-blue-100 rounded-lg mr-2 flex items-center justify-center">
                          <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                        </div>
                        <div className="text-sm text-gray-700">{stat}</div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Floating badges */}
                <div className="absolute -bottom-5 -right-5 flex space-x-2">
                  {['IMAP', 'POP3', 'SMTP', 'Exchange'].map((protocol) => (
                    <span key={protocol} className="px-2 py-1 text-xs font-medium rounded-full bg-white/80 text-gray-700 backdrop-blur-sm">
                      {protocol}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Email Hosting Features</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Professional email solutions designed for business success
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-effect p-8 rounded-xl"
                >
                  <div className="w-16 h-16 bg-primary/20 rounded-lg flex items-center justify-center mb-6 text-primary">
                    {feature.icon}
                  </div>
                  <h3 className="font-orbitron font-semibold text-xl mb-4">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Plans */}
        <section className="py-20 bg-card">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Email Hosting Plans</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Choose the perfect email hosting plan for your business needs
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`glass-effect p-8 rounded-xl relative ${plan.popular ? 'ring-2 ring-primary' : ''}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-8">
                    <h3 className="font-orbitron font-semibold text-2xl mb-4">{plan.name}</h3>
                    <div className="mb-2">
                      <span className="text-4xl font-bold text-primary">{plan.price}</span>
                      <span className="text-muted-foreground ml-2">{plan.period}</span>
                    </div>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-primary-foreground text-xs">✓</span>
                        </div>
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => openDrawer()}
                    className={`w-full ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    Choose Plan
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Our Email Hosting */}
        <section className="py-20 bg-gray-900/50">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-400 to-blue-500">
                  Why Choose Our Email Hosting
                </span>
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Enterprise-grade features with simple pricing
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  title: 'Military-Grade Security',
                  description: 'End-to-end encryption and advanced threat protection',
                  svg: (
                    <svg width="80" height="80" viewBox="0 0 24 24" className="mx-auto mb-4">
                      <path fill="#2DD4BF" d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12c5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11V11.99z"/>
                      <path fill="#0EA5E9" d="M12 11.99V22c-3.72-1.15-6.47-4.82-7-8.94h7v-.07z"/>
                    </svg>
                  )
                },
                {
                  title: '99.9% Uptime',
                  description: 'Guaranteed reliability with redundant infrastructure',
                  svg: (
                    <svg width="80" height="80" viewBox="0 0 24 24" className="mx-auto mb-4">
                      <circle cx="12" cy="12" r="10" fill="none" stroke="#2DD4BF" strokeWidth="2"/>
                      <path fill="#0EA5E9" d="M12 6v6l4 2" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="12" r="1" fill="#0EA5E9"/>
                    </svg>
                  )
                },
                {
                  title: 'Generous Storage',
                  description: '25GB per mailbox with easy expansion options',
                  svg: (
                    <svg width="80" height="80" viewBox="0 0 24 24" className="mx-auto mb-4">
                      <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="#2DD4BF" strokeWidth="2"/>
                      <rect x="7" y="7" width="10" height="4" fill="#0EA5E9" rx="1"/>
                      <rect x="7" y="13" width="6" height="4" fill="#0EA5E9" rx="1"/>
                      <rect x="15" y="13" width="2" height="4" fill="#0EA5E9" rx="1"/>
                    </svg>
                  )
                },
                {
                  title: '24/7 Support',
                  description: 'Expert assistance whenever you need it',
                  svg: (
                    <svg width="80" height="80" viewBox="0 0 24 24" className="mx-auto mb-4">
                      <circle cx="12" cy="12" r="10" fill="none" stroke="#2DD4BF" strokeWidth="2"/>
                      <path fill="#0EA5E9" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2S2 6.477 2 12s4.477 10 10 10zm0-2a8 8 0 1 1 0-16a8 8 0 0 1 0 16zm-1-13h2v2h-2V7zm0 4h2v6h-2v-6z"/>
                    </svg>
                  )
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6 text-center"
                >
                  {feature.svg}
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-300">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <RelatedServices 
              currentService="Email Hosting" 
              services={[
                {
                  title: "Domain Registration",
                  description: "Register your custom domain to use with professional email services.",
                  icon: <Globe className="h-8 w-8" />
                },
                {
                  title: "Web Hosting",
                  description: "Host your website alongside your professional email accounts.",
                  icon: <Server className="h-8 w-8" />
                },
                {
                  title: "Cloud Services",
                  description: "Complementary cloud solutions for your business infrastructure.",
                  icon: <Cloud className="h-8 w-8" />
                }
              ]} 
            />
          </div>
        </section>

        {/* Web Design Locations */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <WebDesignLocations />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="container mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">
                Ready for Professional Email?
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Get started with secure, reliable email hosting that enhances your business communication
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => openDrawer()}
                  className="btn-primary"
                >
                  Start Email Hosting
                </Button>
                <Link href="/contact">
                  <Button className="btn-secondary">
                    Contact Sales Team
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
